import { useState, useCallback } from 'react';
import { evaluateBand, acknowledgeTransition } from '../services/childChaptersAPI';

/**
 * Hook for evaluating a child's age band and handling band transitions.
 * @param {string} memberId - The family member (person) UUID
 */
export function useChildBandEvaluation(memberId) {
  const [transition, setTransition] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Evaluate the child's current band. If a transition occurred the backend
   * returns transition details which we store locally.
   */
  const evaluate = useCallback(async () => {
    if (!memberId) return null;
    setEvaluating(true);
    setError(null);
    try {
      const result = await evaluateBand(memberId);
      if (result.transition) {
        setTransition(result.transition);
      }
      return result;
    } catch (err) {
      // 404 means profile doesn't exist yet — not an error per se
      if (err.response?.status !== 404) {
        console.error('useChildBandEvaluation: Error evaluating band:', err);
        setError(err);
      }
      return null;
    } finally {
      setEvaluating(false);
    }
  }, [memberId]);

  /**
   * Acknowledge (dismiss) the transition notice so it won't show again.
   */
  const acknowledge = useCallback(async () => {
    if (!memberId) return;
    try {
      await acknowledgeTransition(memberId);
      setTransition(null);
    } catch (err) {
      console.error('useChildBandEvaluation: Error acknowledging transition:', err);
    }
  }, [memberId]);

  return {
    transition,
    evaluating,
    error,
    evaluate,
    acknowledge,
  };
}
